/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { FormControl, InputLabel, MenuItem, SelectChangeEvent } from '@mui/material';
import { CoreBox, CoreTypography } from '@youscience/core';
import { DeadlineType, carnegieGroups } from '@common-types';

import { getEnrollmentSize, getSchoolType } from '@utils/schoolInfographics';

import { SCHOOL_RANKING_DATA } from '@constants/applyingSchoolsPlanner';

import { ReducedSchoolCard } from '@components/ReducedSchoolCard';

import useEducationPlannerStore from '@store/EducationPlannerStore';

import {
  sxStyles,
  StyledWrap,
  StyledSelect,
  MenuPropsStyles,
  StyledRadioGroup,
  StyledScoreButtonsWrap,
  StyledScoreRadioButton,
  StyledRadio,
} from './SelectionStep2Planner.styles';

const DEADLINE_DESCRIPTIONS: { [key: string]: string } = {
  [DeadlineType.EARLY_ACTION]:
    'Early action plans are nonbinding. If you apply early action you will receive an early response to their application but do not have to commit to the college until the normal reply date.',
  [DeadlineType.EARLY_DECISION]:
    'Early decision plans are binding. If you are accepted as an early decision applicant, you must attend the college.',
  [DeadlineType.REGULAR_DECISION]:
    'If you are applying regular decision, it is nonbinding and you can apply to as many schools as you’d like.',
  [DeadlineType.REGULAR_DECISION_OUT_OF_STATE]:
    'If you are applying regular decision as an out of state applicant, it is nonbinding and you can apply to as many schools as you’d like.',
  [DeadlineType.NOT_SURE]:
    'It’s ok if you are not sure what type of application you want to submit. Be sure to talk to your parents and school counselor about this, but be sure to update this later!',
  'Not Found': `Looks like we can’t find a deadline for this school. Please check the school's website for the application deadline date and you can add it to the tracker later.`,
} as const;

export const SelectionStep2 = () => {
  const { school, priority, deadlineType } = useEducationPlannerStore((state) => state.schoolSelectionData);
  const selectedSchool = useEducationPlannerStore((state) => state.schoolSelectionData.schoolPreviewInfo);

  const {
    name,
    pictureUrl = '',
    city,
    state,
    address,
    schoolLength,
    totalStudentsEnrolled,
    cost,
    deadlineTypeOptions,
  } = selectedSchool;

  const setSchoolSelectionData = useEducationPlannerStore((state) => state.setSchoolSelectionData);

  const deadlineOptions =
    deadlineTypeOptions.length > 0
      ? [...deadlineTypeOptions, { deadlineType: DeadlineType.NOT_SURE, deadline: '' }]
      : [];

  const [deadlineValue, setDefaultDeadlineType] = useState<string | null>(
    deadlineOptions.length > 0 ? deadlineType || (deadlineOptions[0].deadlineType as string) : null,
  );

  const [deadlineDescription, setDeadlineDescription] = useState<string>('');

  const costPerYear = +cost === 0 ? '' : `${cost} AVG`;
  const schoolArrayType = carnegieGroups.get(schoolLength) as number[];
  const schoolAddress = `${address}. – ${city}, ${state}, US`;

  useEffect(() => {
    void setSchoolSelectionData({ deadlineType: deadlineValue as DeadlineType });

    setDeadlineDescription(DEADLINE_DESCRIPTIONS[deadlineValue || 'Not Found']);
  }, [deadlineValue]);

  const handleDeadlineTypeChange = (event: SelectChangeEvent<unknown>) => {
    setDefaultDeadlineType(event.target.value as string);
  };

  const handlePriorityChange = (event: SelectChangeEvent<unknown>) => {
    void setSchoolSelectionData({ priority: parseInt(event.target.value as string, 10) });
  };

  return (
    <StyledWrap>
      <CoreTypography data-testid="step2Subtitle" sx={sxStyles.subtitle} variant="body2">
        {`You have selected ${school?.label}. If you are sure, please answer the questions below then press the 'Continue' button.`}
      </CoreTypography>

      <ReducedSchoolCard
        title={name}
        pictureUrl={pictureUrl}
        schoolType={getSchoolType(schoolArrayType[0])}
        enrollmentSize={getEnrollmentSize(totalStudentsEnrolled)}
        costPerYear={costPerYear}
        address={schoolAddress}
        isHideActions
      />

      <CoreBox sx={sxStyles.formControlItem}>
        <FormControl fullWidth size="small">
          {deadlineOptions.length > 0 ? (
            <>
              <InputLabel required>Deadline Type</InputLabel>

              <StyledSelect
                data-testid="deadlineType"
                MenuProps={MenuPropsStyles}
                label="Deadline type*"
                onChange={handleDeadlineTypeChange}
                value={deadlineValue}
              >
                {deadlineOptions.map((option) => (
                  <MenuItem key={option.deadlineType as string} value={option.deadlineType as string}>
                    {option.deadlineType}
                  </MenuItem>
                ))}
              </StyledSelect>
            </>
          ) : null}
          <CoreTypography sx={sxStyles.deadlineHelperText} data-testid="deadlineHelperText">
            {deadlineDescription}
          </CoreTypography>
        </FormControl>
      </CoreBox>

      <CoreBox sx={sxStyles.formControlItem}>
        <FormControl fullWidth size="small">
          <CoreTypography sx={sxStyles.priorityLabel}>Priority *</CoreTypography>

          <StyledRadioGroup onChange={handlePriorityChange} sx={sxStyles.radioGroup}>
            <StyledScoreButtonsWrap>
              {Object.entries(SCHOOL_RANKING_DATA).map(([key, value]) => (
                <CoreBox key={value} sx={sxStyles.radioWrap}>
                  <StyledScoreRadioButton isSelected={priority === +key}>
                    <StyledRadio value={key} />

                    <CoreTypography>{key}</CoreTypography>
                  </StyledScoreRadioButton>

                  <CoreTypography sx={sxStyles.scoreLabel}>{value}</CoreTypography>
                </CoreBox>
              ))}
            </StyledScoreButtonsWrap>
          </StyledRadioGroup>
        </FormControl>
      </CoreBox>
    </StyledWrap>
  );
};
